import {
  ActionIcon, Box, Button, FileButton, Group, Image, LoadingOverlay, Stack, Text
} from "@mantine/core";
import { CameraIcon, TrashIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MediaApi } from "../../../apis";
import HttpHandler from "../../../utils/http-handler";
import { message } from "../../../utils/message";

interface Props {
  value: string[];
  onChange: (urls: string[]) => void;
  max?: number;
  error?: string | null;
  disabled?: boolean;
}

// §D10 step 5 — multi-photo evidence. Reuses MediaApi.upload (same endpoint as
// the shared FileUpload component) to turn a picked file into a stored URL.
export function PhotoUploader({ value, onChange, max = 10, error, disabled }: Props) {
  const { t } = useTranslation("returns");
  const [loading, setLoading] = useState(false);
  const atLimit = value.length >= max;

  const onPick = (blob: File | null) => {
    if (!blob) return;
    setLoading(true);
    (async () => {
      try {
        const form = new FormData();
        form.append("file", blob);
        const res = await MediaApi.upload(form);
        const url = (res as { url?: string })?.url;
        if (url) onChange([...value, url]);
      } catch (err) {
        message.error((err as HttpHandler)?.message ?? t("photoUploader.uploadError"));
      } finally {
        setLoading(false);
      }
    })();
  };

  const removeAt = (index: number) => onChange(value.filter((_, i) => i !== index));

  return (
    <Stack gap="xs">
      <Box pos="relative">
        <LoadingOverlay visible={loading} loaderProps={{ size: "sm" }} />
        <Group gap="sm">
          {value.map((url, i) => (
            <Box key={`${url}-${i}`} pos="relative" w={80} h={80}>
              <Image src={url} alt={t("photoUploader.photoAlt", { number: i + 1 })} w={80} h={80} radius="md" fit="cover" />
              {!disabled ? (
                <ActionIcon
                  color="dark"
                  variant="filled"
                  size="sm"
                  radius="xl"
                  pos="absolute"
                  top={-6}
                  right={-6}
                  onClick={() => removeAt(i)}
                  aria-label={t("photoUploader.removePhoto")}
                >
                  <TrashIcon size={14} />
                </ActionIcon>
              ) : null}
            </Box>
          ))}
          {!atLimit && !disabled ? (
            <FileButton onChange={onPick} accept="image/*">
              {(props) => (
                <Button
                  {...props}
                  variant="default"
                  w={80}
                  h={80}
                  p={0}
                  leftSection={<CameraIcon size={20} />}
                  styles={{
                    root: { borderStyle: "dashed" },
                    inner: { flexDirection: "column", gap: "var(--mantine-spacing-xs)" },
                    section: { margin: 0 },
                    label: { height: "auto" },
                  }}
                >
                  <Text fz={14}>{t("photoUploader.add")}</Text>
                </Button>
              )}
            </FileButton>
          ) : null}
          {!error && <Text fz={14} c="gray.8">{t("photoUploader.countLabel", { current: value.length, max })}</Text>}
        </Group>
      </Box>
      {error && <Text fz={14} fw={600}>{error}</Text>}
    </Stack>
  );
}
